/**
 * Outil « Courbe paramétrée » (Boîte à outils, MPSI/PTSI → PT).
 *
 * Explorer : une courbe t ↦ f(t) = (x(t), y(t)) choisie parmi neuf ; un
 * curseur fait avancer t, M(t) parcourt la courbe et l'arc parcouru se
 * colore (abscisse curviligne s(t), longueur totale). En M(t) : f′(t) qui
 * dirige la tangente (ou f⁽ᵖ⁾ en un point singulier), tangente horizontale /
 * verticale, repère de Frenet (T, N), courbure γ et cercle osculateur.
 * Points singuliers : zoom sur le point, f′(t₀) = 0, premier p avec
 * f⁽ᵖ⁾(t₀) ≠ 0, premier q non colinéaire, allure selon les parités (tableau).
 * Défi : tangente, point singulier, allure, courbure, longueur.
 * Logique : `curve.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  Note,
  PRIMARY_BUTTON,
  ParamSlider,
  SECONDARY_BUTTON,
  Segmented,
  ToggleChip,
} from '../../ui/controls';
import { makeFrame, type Frame } from '../../ui/frame';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import { FIGURE_BG_CLASS, HaloText, PlotGrid, VectorArrow } from '../../ui/svg-parts';
import {
  PRESETS,
  QUIZ_KINDS,
  SHAPE_TEXT,
  arcLength,
  dec,
  fDer,
  local,
  makeQuiz,
  piText,
  point,
  presetById,
  shapeOf,
  singularInfo,
  speed,
  type Preset,
  type Quiz,
  type Shape,
} from './curve';

const SIZE = 320;
const SELECT =
  'border-ink-200 text-ink-900 min-h-11 w-full rounded-xl border bg-white px-2 text-[15px]';
const CURVE_FADED = 'var(--color-ink-300)';
const ARC = 'var(--color-primary-500)';
const VEL = 'var(--color-accent-600)';
const FRENET = 'var(--color-success-600)';
const PI = Math.PI;

/** fenêtre carrée autour d'un nuage de points (marge 15 %) */
function squareFrame(pts: { x: number; y: number }[]): Frame {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const half =
    (Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 0.2) / 2) *
    1.18;
  return makeFrame({ xMin: cx - half, xMax: cx + half, yMin: cy - half, yMax: cy + half }, SIZE);
}

function sample(p: Preset, a: number, b: number, n = 600) {
  return Array.from({ length: n + 1 }, (_, i) => point(p, a + ((b - a) * i) / n));
}

/** pas des graduations : au plus 6 intervalles dans la fenêtre */
function gridStep(fr: Frame) {
  const span = fr.vp.xMax - fr.vp.xMin;
  return [0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10].find((st) => span / st <= 6) ?? 20;
}

const pathOf = (pts: { x: number; y: number }[], fr: Frame) =>
  pts.map((q, i) => `${i ? 'L' : 'M'}${fr.tx(q.x).toFixed(1)} ${fr.ty(q.y).toFixed(1)}`).join('');

function PresetPicker({
  value,
  onChange,
  presets,
}: {
  value: string;
  onChange: (id: string) => void;
  presets: readonly Preset[];
}) {
  return (
    <label className="block">
      <span className="text-ink-700 mb-1 block text-sm font-semibold">Courbe</span>
      <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        {presets.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ---------- explorer ---------- */

/** curseur : t = kπ/24 (courbes trigonométriques) ou t au pas de 0,05 */
function useParam(p: Preset) {
  const kMin = Math.round((p.a * 24) / PI);
  const kMax = Math.round((p.b * 24) / PI);
  const start = p.trig
    ? Math.round((kMin + kMax) / 2 + (kMax - kMin) / 8)
    : Math.round(((p.a + p.b) / 2 + (p.b - p.a) / 8) * 20) / 20;
  const [v, setV] = useState(start);
  const t = p.trig ? (v * PI) / 24 : v;
  const text = p.trig ? piText(v) : dec(v, 2);
  return {
    v,
    setV,
    t,
    text,
    min: p.trig ? kMin : p.a,
    max: p.trig ? kMax : p.b,
    step: p.trig ? 1 : 0.05,
  };
}

function Explore({ preset }: { preset: Preset }) {
  const P = useParam(preset);
  const [vel, setVel] = useState(true);
  const [frenet, setFrenet] = useState(false);
  const [osc, setOsc] = useState(false);
  const pts = useMemo(() => sample(preset, preset.a, preset.b), [preset]);
  const frame = useMemo(() => squareFrame(pts), [pts]);
  const span = frame.vp.xMax - frame.vp.xMin;
  const total = useMemo(() => arcLength(preset, preset.a, preset.b), [preset]);
  // échelle de f′ : la plus grande vitesse tient dans 30 % de la fenêtre
  const velScale = useMemo(() => {
    let vmax = 0;
    for (let i = 0; i <= 200; i++)
      vmax = Math.max(vmax, speed(preset, preset.a + ((preset.b - preset.a) * i) / 200));
    return [1, 2, 4, 5, 10, 20, 50].find((k) => vmax / k <= 0.3 * span) ?? 100;
  }, [preset, span]);

  const L = local(preset, P.t);
  const sing = L.regular ? null : singularInfo(preset, P.t);
  const s = arcLength(preset, preset.a, P.t, 300);
  const traced = pts.filter((_, i) => preset.a + ((preset.b - preset.a) * i) / 600 <= P.t + 1e-12);
  const mx = frame.tx(L.x);
  const my = frame.ty(L.y);
  const u = frame.unit;
  // direction de la tangente : f′(t), ou f⁽ᵖ⁾(t) en un point singulier
  const dir = L.regular ? { x: L.dx, y: L.dy } : sing ? sing.ders[sing.p - 1]! : null;
  const dn = dir ? Math.hypot(dir.x, dir.y) : 0;
  const tanHalf = 0.45 * span;
  const R = L.gamma && Math.abs(L.gamma) > 1e-9 ? 1 / Math.abs(L.gamma) : null;

  const t0Text = preset.trig ? piText(Math.round((preset.a * 24) / PI)) : dec(preset.a, 2);
  const t1Text = preset.trig ? piText(Math.round((preset.b * 24) / PI)) : dec(preset.b, 2);
  const horiz = L.regular && Math.abs(L.dy) < 1e-9;
  const vert = L.regular && Math.abs(L.dx) < 1e-9;
  const lines = [
    `t = ${P.text} : M(t) = (${dec(L.x)} ; ${dec(L.y)}).`,
    L.regular
      ? `f′(t) = (${dec(L.dx)} ; ${dec(L.dy)}) ≠ 0 : point régulier, f′(t) dirige la tangente${horiz ? ' (horizontale : y′ = 0)' : vert ? ' (verticale : x′ = 0)' : ''}.`
      : sing
        ? `f′(t) = (0 ; 0) : point singulier. ${fDer(sing.p)}(t) = (${dec(sing.ders[sing.p - 1]!.x)} ; ${dec(sing.ders[sing.p - 1]!.y)}) dirige la tangente ; p = ${sing.p}, q = ${sing.q} : ${SHAPE_TEXT[sing.shape]}.`
        : 'f′(t) = (0 ; 0) : point singulier.',
    ...(L.regular && L.gamma !== null
      ? [
          `Courbure γ = (x′y″ − x″y′)/(x′² + y′²)^(3/2) ≈ ${dec(L.gamma)}${R ? ` ; rayon de courbure R = 1/|γ| ≈ ${dec(R)}` : ' (nulle ici)'}.`,
        ]
      : []),
    `Abscisse curviligne s(t) ≈ ${dec(s, 2)} : longueur de l’arc parcouru depuis t = ${t0Text} (en couleur), intégrale de ‖f′(u)‖ pour u de ${t0Text} à t.`,
    `Longueur de l’arc tracé (t ∈ [${t0Text} ; ${t1Text}]) ≈ ${dec(total, 2)}${preset.length ? ` (valeur exacte : ${preset.length.text})` : ''}.`,
    // une option demandée qui ne peut rien montrer : on dit pourquoi
    ...(!L.regular && (frenet || osc)
      ? ['Repère de Frenet, courbure et cercle osculateur ne sont pas définis ici : f′(t) = 0.']
      : []),
    ...(L.regular && osc && !R
      ? ['Courbure nulle : pas de cercle osculateur (rayon infini).']
      : []),
    ...(L.regular && osc && R && R >= 3 * span
      ? [`Cercle osculateur hors du cadre (R ≈ ${dec(R, 2)}).`]
      : []),
  ];
  const label = `Courbe ${preset.label}, point M(t) pour t = ${P.text} en (${dec(L.x, 2)} ; ${dec(L.y, 2)}).`;
  const kLabel = velScale === 1 ? 'f′(t)' : `f′(t)/${velScale}`;

  return (
    <div className="space-y-3">
      <ParamSlider
        name="Paramètre t"
        ariaLabel="Paramètre t"
        value={P.v}
        min={P.min}
        max={P.max}
        step={P.step}
        onChange={P.setV}
        format={() => frenchSpaces(`t = ${P.text}`)}
      />
      <div className="flex flex-wrap gap-2">
        <ToggleChip on={vel} onClick={() => setVel(!vel)}>
          Vitesse f′(t)
        </ToggleChip>
        <ToggleChip on={frenet} onClick={() => setFrenet(!frenet)}>
          Repère de Frenet
        </ToggleChip>
        <ToggleChip on={osc} onClick={() => setOsc(!osc)}>
          Cercle osculateur
        </ToggleChip>
      </div>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
      >
        <PlotGrid frame={frame} step={gridStep(frame)} />
        <path d={pathOf(pts, frame)} fill="none" stroke={CURVE_FADED} strokeWidth={2} />
        {traced.length > 1 && (
          <path d={pathOf(traced, frame)} fill="none" stroke={ARC} strokeWidth={3.5} />
        )}
        {preset.singular.map((sg) => {
          const q = point(preset, sg.t);
          return (
            <rect
              key={sg.label}
              x={frame.tx(q.x) - 5}
              y={frame.ty(q.y) - 5}
              width={10}
              height={10}
              fill="var(--fig-bg, #fff)"
              stroke="var(--color-ink-700)"
              strokeWidth={1.75}
            />
          );
        })}
        {osc && R && L.N && R < 3 * span && (
          <circle
            cx={frame.tx(L.x + L.N.x / L.gamma!)}
            cy={frame.ty(L.y + L.N.y / L.gamma!)}
            r={R * u}
            fill="none"
            stroke={FRENET}
            strokeWidth={1.75}
            strokeDasharray="5 4"
          />
        )}
        {dir && dn > 0 && (
          <line
            x1={frame.tx(L.x - (dir.x / dn) * tanHalf)}
            y1={frame.ty(L.y - (dir.y / dn) * tanHalf)}
            x2={frame.tx(L.x + (dir.x / dn) * tanHalf)}
            y2={frame.ty(L.y + (dir.y / dn) * tanHalf)}
            stroke="var(--color-ink-700)"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
        )}
        {vel && L.regular && (
          <g>
            <VectorArrow
              x1={mx}
              y1={my}
              x2={frame.tx(L.x + L.dx / velScale)}
              y2={frame.ty(L.y + L.dy / velScale)}
              color={VEL}
              width={3}
            />
            <HaloText
              x={frame.tx(L.x + L.dx / velScale) + (L.dx >= 0 ? 6 : -6)}
              y={frame.ty(L.y + L.dy / velScale) - 8}
              textAnchor={L.dx >= 0 ? 'start' : 'end'}
              fontSize={13}
              fontWeight={700}
              fill={VEL}
            >
              {kLabel}
            </HaloText>
          </g>
        )}
        {frenet && L.T && L.N && (
          <g>
            {(['T', 'N'] as const).map((k) => {
              const w = L[k]!;
              const ex = frame.tx(L.x + w.x * 0.22 * span);
              const ey = frame.ty(L.y + w.y * 0.22 * span);
              return (
                <g key={k}>
                  <VectorArrow x1={mx} y1={my} x2={ex} y2={ey} color={FRENET} width={2.5} />
                  <HaloText
                    x={k === 'T' ? ex - L.N!.x * 14 : ex + w.x * 12}
                    y={k === 'T' ? ey + L.N!.y * 14 : ey - w.y * 12}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={13}
                    fontWeight={700}
                    fill={FRENET}
                  >
                    {k}
                  </HaloText>
                </g>
              );
            })}
          </g>
        )}
        <circle cx={mx} cy={my} r={6} fill="var(--color-ink-900)" />
        <HaloText
          x={mx - 10}
          y={my + 18}
          fontSize={14}
          fontWeight={700}
          fill="var(--color-ink-900)"
        >
          M
        </HaloText>
      </svg>
      <p className="sr-only" aria-live="polite">
        {frenchSpaces(lines[0]! + ' ' + lines[1]!)}
      </p>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="pc-line" className="mb-1 block last:mb-0">
            {frenchSpaces(l).replace(/\b([pq]) = /g, '$1\u00a0= ')}
          </span>
        ))}
      </Note>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `${preset.note} Valeurs approchées (≈) ; ${velScale === 1 ? 'f′(t) est à l’échelle' : `f′(t) est représenté à l’échelle 1/${velScale}`}, T et N sont unitaires.`,
        )}
      </p>
    </div>
  );
}

/* ---------- points singuliers ---------- */

const SING_PRESETS = PRESETS.filter((p) => p.singular.length > 0);
/** 2 × 2 : ligne = parité de p, colonne = parité de q (pair, impair) */
const SHAPE_CELLS: { shape: Shape; p: string; q: string }[] = [
  { shape: 'ordinaire', p: 'p impair', q: 'q pair' },
  { shape: 'inflexion', p: 'p impair', q: 'q impair' },
  { shape: 'rebroussement2', p: 'p pair', q: 'q pair' },
  { shape: 'rebroussement1', p: 'p pair', q: 'q impair' },
];

function Singular({ preset }: { preset: Preset }) {
  const [k, setK] = useState(0);
  const sg = preset.singular[Math.min(k, preset.singular.length - 1)]!;
  const info = singularInfo(preset, sg.t);
  const delta = preset.trig ? 0.55 : 0.6;
  // de part et d'autre du point, même au bord de l'intervalle tracé (courbes définies sur ℝ)
  const a = sg.t - delta;
  const b = sg.t + delta;
  const pts = useMemo(() => sample(preset, a, b, 300), [preset, a, b]);
  const frame = useMemo(() => squareFrame(pts), [pts]);
  const span = frame.vp.xMax - frame.vp.xMin;
  const M = point(preset, sg.t);
  if (!info) return null;
  const P = info.ders[info.p - 1]!;
  const Q = info.ders[info.q - 1]!;
  const arrow = (w: { x: number; y: number }) => {
    const n = Math.hypot(w.x, w.y);
    return { x: M.x + (w.x / n) * 0.3 * span, y: M.y + (w.y / n) * 0.3 * span };
  };
  const ap = arrow(P);
  const aq = arrow(Q);
  const pn = Math.hypot(P.x, P.y);
  const lines = [
    ...info.ders.slice(0, info.q).map((d, i) => {
      const k1 = i + 1;
      const v = `${fDer(k1)}(t₀) = (${dec(d.x)} ; ${dec(d.y)})`;
      if (k1 < info.p) return `${v}`;
      if (k1 === info.p) return `${v} ≠ 0 : p = ${info.p}, ce vecteur dirige la tangente.`;
      if (k1 < info.q)
        return d.x === 0 && d.y === 0
          ? `${v} : vecteur nul.`
          : `${v} : colinéaire à ${fDer(info.p)}(t₀).`;
      return `${v} : premier vecteur non colinéaire, q = ${info.q}.`;
    }),
    `p ${info.p % 2 ? 'impair' : 'pair'}, q ${info.q % 2 ? 'impair' : 'pair'} : ${SHAPE_TEXT[info.shape]}.`,
  ];
  const label = `Zoom sur le point singulier t₀ = ${sg.label} de la courbe ${preset.label} : ${SHAPE_TEXT[info.shape]}.`;

  return (
    <div className="space-y-3">
      {preset.singular.length > 1 && (
        <Segmented
          ariaLabel="Point singulier"
          value={String(k)}
          options={preset.singular.map((s, i) => ({ value: String(i), label: `t₀ = ${s.label}` }))}
          onChange={(v) => setK(Number(v))}
        />
      )}
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={frenchSpaces(label)}
        className={cn(
          'border-ink-100 mx-auto block w-full max-w-[360px] rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
      >
        <PlotGrid frame={frame} step={gridStep(frame)} labels={false} />
        <line
          x1={frame.tx(M.x - (P.x / pn) * span)}
          y1={frame.ty(M.y - (P.y / pn) * span)}
          x2={frame.tx(M.x + (P.x / pn) * span)}
          y2={frame.ty(M.y + (P.y / pn) * span)}
          stroke="var(--color-ink-700)"
          strokeWidth={1.5}
          strokeDasharray="6 4"
        />
        <path d={pathOf(pts, frame)} fill="none" stroke={ARC} strokeWidth={3} />
        <VectorArrow
          x1={frame.tx(M.x)}
          y1={frame.ty(M.y)}
          x2={frame.tx(ap.x)}
          y2={frame.ty(ap.y)}
          color={VEL}
          width={3}
        />
        <VectorArrow
          x1={frame.tx(M.x)}
          y1={frame.ty(M.y)}
          x2={frame.tx(aq.x)}
          y2={frame.ty(aq.y)}
          color={FRENET}
          width={3}
        />
        {(
          [
            [ap, P, `${fDer(info.p)}(t₀)`, VEL],
            [aq, Q, `${fDer(info.q)}(t₀)`, FRENET],
          ] as const
        ).map(([e, w, txt, c]) => {
          const n = Math.hypot(w.x, w.y);
          return (
            <HaloText
              key={txt}
              x={frame.tx(e.x) + (w.x / n) * 14}
              y={frame.ty(e.y) - (w.y / n) * 14}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={13}
              fontWeight={700}
              fill={c}
            >
              {txt}
            </HaloText>
          );
        })}
        <circle cx={frame.tx(M.x)} cy={frame.ty(M.y)} r={5} fill="var(--color-ink-900)" />
      </svg>
      <Note live={false}>
        {lines.map((l) => (
          <span key={l} data-testid="pc-sing" className="mb-1 block last:mb-0">
            {frenchSpaces(l).replace(/\b([pq]) = /g, '$1\u00a0= ')}
          </span>
        ))}
      </Note>
      <ul
        aria-label="Allure selon les parités de p et q"
        className="grid grid-cols-2 gap-2 text-sm"
      >
        {SHAPE_CELLS.map((c) => {
          const on = c.shape === shapeOf(info.p, info.q);
          return (
            <li
              key={c.shape}
              aria-current={on ? 'true' : undefined}
              className={cn(
                'rounded-xl border px-3 py-2',
                on
                  ? 'border-primary-500 bg-primary-50 text-ink-900'
                  : 'border-ink-100 text-ink-700',
              )}
            >
              <span className="block text-xs font-semibold">{`${c.p}, ${c.q}`}</span>
              {SHAPE_TEXT[c.shape]}
            </li>
          );
        })}
      </ul>
      <p className="text-ink-500 text-xs">
        {frenchSpaces(
          `${preset.note} Dérivées calculées par développement de Taylor en t₀ (valeurs exactes aux arrondis près).`,
        )}
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed }: { seed?: number }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  const [q, setQ] = useState<Quiz>(() => makeQuiz(QUIZ_KINDS[0]!, rnd));
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQ(makeQuiz(QUIZ_KINDS[c - 1]!, rnd));
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        {frenchSpaces(q.prompt)}
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-center text-base font-semibold tabular-nums transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            {frenchSpaces(o.text)}
          </button>
        ))}
      </div>
      <div aria-live="polite">
        {chosen && (
          <Note live={false} tone={chosen.correct ? 'success' : 'error'}>
            <span data-testid="pc-feedback">
              {frenchSpaces(
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`,
              )}
            </span>
          </Note>
        )}
      </div>
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- outil ---------- */

export function ParamCurveTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'explore' | 'singular' | 'quiz'>('explore');
  const [id, setId] = useState('lissajous');
  const [singId, setSingId] = useState('cusp');
  const preset = presetById(id);
  const singPreset = presetById(singId);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'singular', label: 'Points singuliers' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'} className="space-y-3">
        <PresetPicker value={id} onChange={setId} presets={PRESETS} />
        <Explore key={preset.id} preset={preset} />
      </div>
      <div hidden={mode !== 'singular'} className="space-y-3">
        <PresetPicker value={singId} onChange={setSingId} presets={SING_PRESETS} />
        <Singular key={singPreset.id} preset={singPreset} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} />
      </div>
    </div>
  );
}
